import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

interface ChartFrameProps {
  title?: string;
  /** Leyenda de series (se usa cuando hay más de una o en gráficos circulares). */
  legend?: ReactNode;
  children: ReactNode;
  className?: string;
}

/**
 * Marco de un gráfico: título, leyenda y superficie.
 * Los gráficos (`LineChart`, `BarChart`, `DonutChart`) sólo dibujan el SVG.
 *
 * El contenido va en un contenedor `min-w-0 overflow-hidden` para que ningún
 * gráfico pueda desbordar la tarjeta que lo contiene.
 */
export function ChartFrame({ title, legend, children, className }: ChartFrameProps) {
  return (
    <figure className={cn('mt-3 min-w-0 overflow-hidden rounded-xl border border-ink-100 bg-white p-3', className)}>
      {title ? (
        <figcaption className="mb-2 text-xs font-semibold tracking-tight text-ink-700">
          {title}
        </figcaption>
      ) : null}

      <div className="min-w-0">{children}</div>

      {legend ? <div className="mt-2">{legend}</div> : null}
    </figure>
  );
}

interface ChartLegendProps {
  items: { label: string; color: string; value?: string }[];
}

/** Leyenda reutilizable (series de línea/barra y porciones del donut). */
export function ChartLegend({ items }: ChartLegendProps) {
  return (
    <ul className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
      {items.map((item) => (
        <li key={item.label} className="flex items-center gap-1.5 text-[11px] text-ink-600">
          <span
            aria-hidden="true"
            className="size-2 shrink-0 rounded-full"
            style={{ backgroundColor: item.color }}
          />
          <span className="max-w-[10rem] truncate">{item.label}</span>
          {item.value ? <span className="font-mono text-ink-400">{item.value}</span> : null}
        </li>
      ))}
    </ul>
  );
}
